import * as React from "react";

import { Typography, TypographyProps } from "@mui/material";
import { StandardCSSProperties } from "@mui/system";
import {
  childrenToString,
  useGetTextLang,
} from "foundations/utils/useGetTextLang";

type TextSize = "xs" | "s" | "m" | "l" | "xl" | "xxl";
type TextColor =
  | "normal"
  | "secondary"
  | "light"
  | "primaryDark"
  | "primary"
  | "primaryLight"
  | "disabled"
  | "secondaryPurple"
  | "secondaryLightPurple"
  | "blue"
  | "success"
  | "warning"
  | "error"
  | "white"
  | "inherit";

export interface TextProps
  extends
    Pick<
      TypographyProps,
      | "children"
      | "component"
      | "id"
      | "noWrap"
      | "align"
      | "gutterBottom"
      | "sx"
    >,
    React.ComponentPropsWithRef<"p"> {
  /** @default "normal" (dark text) */
  color?: TextColor;
  /** @default "m" (1rem) */
  size?: TextSize;
  customSize?: string;
  bold?: boolean;
  italic?: boolean;
  letterCase?: "capitalize" | "uppercase" | "lowercase" | "none";
  mx?: StandardCSSProperties["marginLeft"];
  my?: StandardCSSProperties["marginTop"];
  mr?: StandardCSSProperties["marginRight"];
  ml?: StandardCSSProperties["marginLeft"];
  mb?: StandardCSSProperties["marginBottom"];
  mt?: StandardCSSProperties["marginTop"];
  noTranslate?: boolean;
  lineHeight?: number;
}

const COLORS: Record<TextColor, string> = {
  normal: "text.primary",
  secondary: "text.secondary",
  light: "text.light",
  primary: "primary.main",
  primaryLight: "primary.light",
  primaryDark: "primary.dark",
  secondaryPurple: "secondary.main",
  secondaryLightPurple: "secondary.light",
  disabled: "text.disabled",
  blue: "blue.main",
  success: "success.text",
  warning: "warning.text",
  error: "error.text",
  white: "white.main",
  inherit: "inherit",
};

const FONT_SIZES: Record<TextSize, string> = {
  xs: "0.75rem",
  s: "0.875rem",
  m: "1rem",
  l: "1.125rem",
  xl: "1.25rem",
  xxl: "1.5rem",
};

export function Text(props: Readonly<TextProps>) {
  const getTextLang = useGetTextLang();
  const {
    color = "normal",
    size = "m",
    customSize,
    bold,
    italic,
    letterCase,
    noTranslate,
    lineHeight,
    ...rest
  } = props;

  return (
    <Typography
      className={noTranslate ? "notranslate" : ""}
      lang={
        noTranslate ? undefined : getTextLang(childrenToString(props.children))
      }
      {...rest}
      sx={[
        {
          color: COLORS[color],
          fontSize: customSize ?? FONT_SIZES[size],
          fontWeight: bold ? "bold" : "normal",
          fontStyle: italic ? "italic" : "normal",
          textTransform: letterCase,
          lineHeight: lineHeight,
          fontVariantLigatures: "no-common-ligatures",
          mx: props.mx,
          my: props.my,
          mr: props.mr,
          ml: props.ml,
          mb: props.mb,
          mt: props.mt,
          ...props.sx,
        },
        ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
      ]}
    />
  );
}
